/* istanbul ignore file */
// TODO: ^ ignore coverage on this file until we can have app test scaffolding ready
import React from 'react'
import { connect } from 'react-redux'

import ParseEntry from 'utils/contentfulParser'
import {
  resolveRouteServiceApp,
  showSiteSelector,
  isHighSchoolUrl,
  isK5Url,
  isMiddleSchoolUrl,
} from 'utils/routing/routeHelpers'
import { RouteContentProductAppMap } from 'utils/routing/routeProductMaps'
import {
  isPreviewMode,
  transformInstructorDataToOldShape,
} from 'utils/highschoolHelpers'
import { isFlagOn } from 'utils/featureFlags'
import { FlaggedRouteContentGetter as RouteContentGetter } from 'layers/content/v2/Hocs/FlaggedRouteContentGetter'
import withApiError from 'layers/errorHandling/apiError'
import { selectors as lmsSelectors } from 'store/lmsManager'
import { FlaggedCourseManagerGetter as CourseManagerGetter } from 'layers/content/v2/Hocs/FlaggedCourseManagerGetter'
import { programs } from 'layers/content/v2/hooks/useCourseManager'
import ResourcesContent from './component'
import { determinePageLayout } from './pageLayouts'
import {
  getCompletedTrainingProgramNodesCount,
  getInstructorDataForCurrentProgram,
  getTotalTrainingNodesCount,
  getCompletedK8TrainingNodesCount,
  formatTrainingProgressString,
  getTrainingCourseTreeName,
} from './helpers'

const ResourcesContentContainer = props => {
  const {
    children,
    getEntryWithNewLocale,
    isDistrictSupportsPage,
    isFamilyResourcesPage,
    isSelaLeaderPage,
    isSelaProduct,
    match,
    routeContent,
    highSchoolInstructorData,
    productTabTitle,
    userContextManager,
    courseManager,
    instructorData,
  } = props || {}

  const { content = {}, route = {}, loading } = routeContent
  let { courseNodeId = '' } = route || {}
  const { article } = content
  if (!courseNodeId) {
    const { courseNodeConnector } = article || {}
    if (courseNodeConnector) {
      // fallback case: extract courseNodeId from the article content
      // (because it's not available in some cases from route-service
      courseNodeId = courseNodeConnector.courseNodeEntryPoint
    }
  }
  let parsedEntry

  if (content) {
    parsedEntry = mapper(article) || {}
  }

  const readyToLoad = !!content && !loading

  // Determine program type and calculate training progress
  const isHighSchool = isHighSchoolUrl()
  const isK5 = isK5Url()
  const isMs = isMiddleSchoolUrl()

  let numberOfCompletedTrainingPrograms

  if (isHighSchool) {
    // For High School, use the original logic
    numberOfCompletedTrainingPrograms = getCompletedTrainingProgramNodesCount(
      highSchoolInstructorData,
    )
  } else if (isK5 || isMs) {
    // For K8 programs, calculate both completed and total counts
    const completedCount = getCompletedK8TrainingNodesCount(
      instructorData,
      isK5,
      isMs,
    )

    const trainingCourseTreeName = getTrainingCourseTreeName(isK5, isMs)
    const trainingCourseTree =
      courseManager?.courseTrees?.[trainingCourseTreeName]
    const totalCount = getTotalTrainingNodesCount(trainingCourseTree)

    numberOfCompletedTrainingPrograms = formatTrainingProgressString(
      completedCount,
      totalCount,
      false, // not high school
    )
  } else {
    // Fallback for other programs
    numberOfCompletedTrainingPrograms = getCompletedTrainingProgramNodesCount(
      highSchoolInstructorData,
    )
  }

  const shouldShowSiteSelector = showSiteSelector({
    checkMs: true,
    checkK5: true,
    checkHs: true,
  })

  if (!readyToLoad) {
    return null
  }

  return (
    <ResourcesContent
      {...parsedEntry}
      courseNodeId={courseNodeId}
      getEntryWithNewLocale={getEntryWithNewLocale}
      isDistrictSupportsPage={isDistrictSupportsPage}
      isFamilyResourcesPage={isFamilyResourcesPage}
      isHsPreview={isPreviewMode()}
      isSelaLeaderPage={isSelaLeaderPage}
      isSelaProduct={isSelaProduct}
      match={match}
      numberOfCompletedTrainingPrograms={numberOfCompletedTrainingPrograms}
      productTabTitle={productTabTitle}
      shouldShowSiteSelector={shouldShowSiteSelector}
      userContextManager={userContextManager}
    >
      {children}
    </ResourcesContent>
  )
}

const mapper = entry => {
  if (entry) {
    const { markComplete } = entry || {}
    const { derivedContent, rootContent, content } = ParseEntry(
      entry,
      isPreviewMode(),
      isFlagOn(['high-school-june-release']),
    )
    const { anchorNavigation } = derivedContent || {}
    const { landingBanner } = rootContent || {}
    const pageLayout = determinePageLayout(anchorNavigation)
    const currentLocale = entry && entry.locale
    entry = {
      anchorNavigation,
      content,
      landingBanner,
      markComplete,
      pageLayout,
      currentLocale,
    }

    return entry
  }

  return null
}

const mapStateToProps = state => {
  const { productManager, userContextManager } = state

  return {
    highSchoolInstructorData: transformInstructorDataToOldShape(
      lmsSelectors.selectHsInstructorData(state),
    ),
    instructorData: getInstructorDataForCurrentProgram(state),
    productTabTitle: productManager?.productMap?.name || '',
    userContextManager,
  }
}

const options = {
  app: ({ route }) =>
    route.includes('family-resources')
      ? RouteContentProductAppMap.learnOrphans
      : resolveRouteServiceApp(route),
  routeResolver: () => window?.location?.pathname,
}

// Determine which program to use for course manager based on current URL
const programResolver = () => {
  if (isHighSchoolUrl()) {
    return programs.highSchool
  } else if (isK5Url()) {
    return programs.elementaryTrainingOnly
  } else if (isMiddleSchoolUrl()) {
    return programs.middleSchoolTrainingOnly
  }
  return programs.none
}

const ConnectedResourcesContentContainer = connect(mapStateToProps)(
  ResourcesContentContainer,
)

const ResourcesContentWithCourseManager = CourseManagerGetter(
  ConnectedResourcesContentContainer,
  { programResolver },
)

export default RouteContentGetter(
  withApiError(ResourcesContentWithCourseManager),
  options,
)
